/**
 * AuthSmash Matrix Filters - Quick toggle filters above the matrix
 */

import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle, faExclamationTriangle, faQuestionCircle,
  faCode, faFile, faUndo
} from '@fortawesome/free-solid-svg-icons';

const AuthSmashMatrixFilters = ({ filters, onFilterChange, counts, isDarkMode, excludeStatic, onExcludeStaticChange, totalEndpoints, filteredCount, searchText, onSearchChange }) => {
  const { enforcement, methods, contentTypes } = filters;

  const toggleEnforcement = (type) => {
    const newSet = new Set(enforcement);
    if (newSet.has(type)) {
      newSet.delete(type);
    } else {
      newSet.add(type);
    }
    onFilterChange('enforcement', Array.from(newSet));
  };

  const toggleMethod = (method) => {
    const newSet = new Set(methods);
    if (newSet.has(method)) {
      newSet.delete(method);
    } else {
      newSet.add(method);
    }
    onFilterChange('methods', Array.from(newSet));
  };

  const toggleContentType = (type) => {
    const newSet = new Set(contentTypes);
    if (newSet.has(type)) {
      newSet.delete(type);
    } else {
      newSet.add(type);
    }
    onFilterChange('contentTypes', Array.from(newSet));
  };

  const reset = () => {
    onFilterChange('reset');
  };

  const badgeClass = (active) => `
    px-1.5 py-0.5 rounded text-[10px] font-medium cursor-pointer transition-all
    ${active
      ? 'ring-1 ring-offset-1' + (isDarkMode ? ' ring-offset-gray-900' : ' ring-offset-white')
      : 'opacity-50 hover:opacity-75'
    }
  `;

  return (
    <div className={`mb-1 p-1.5 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
      {/* All filters + controls in ONE compact row */}
      <div className="flex flex-wrap items-center gap-1">
          <button
            onClick={() => toggleEnforcement('enforced')}
            className={`${badgeClass(enforcement.includes('enforced'))} bg-green-600 text-white hover:bg-green-700`}
          >
            <FontAwesomeIcon icon={faCheckCircle} className="mr-1" />
            Enforced
            <span className="ml-1 opacity-75">({counts.enforced || 0})</span>
          </button>

          <button
            onClick={() => toggleEnforcement('bypassed')}
            className={`${badgeClass(enforcement.includes('bypassed'))} bg-red-600 text-white hover:bg-red-700`}
          >
            <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1" />
            Bypassed
            <span className="ml-1 opacity-75">({counts.bypassed || 0})</span>
          </button>

          <button
            onClick={() => toggleEnforcement('partial')}
            className={`${badgeClass(enforcement.includes('partial'))} bg-yellow-600 text-white hover:bg-yellow-700`}
          >
            <FontAwesomeIcon icon={faQuestionCircle} className="mr-1" />
            Review
            <span className="ml-1 opacity-75">({counts.partial || 0})</span>
          </button>

          {/* Separator */}
          <div className={`h-6 w-px ${isDarkMode ? 'bg-gray-600' : 'bg-gray-300'}`}></div>

          {/* HTTP Method Filters */}
          {['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map(method => {
            const count = counts.methods?.[method] || 0;
            if (count === 0) return null;

            return (
              <button
                key={method}
                onClick={() => toggleMethod(method)}
                className={`${badgeClass(methods.includes(method))} ${
                  isDarkMode ? 'bg-blue-700 text-white hover:bg-blue-600' : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {method}
                <span className="ml-1.5 opacity-75">({count})</span>
              </button>
            );
          })}

          {/* Separator */}
          <div className={`h-6 w-px ${isDarkMode ? 'bg-gray-600' : 'bg-gray-300'}`}></div>

          {/* Content Type Filters */}
          {[
            { key: 'json', label: 'JSON', icon: faCode },
            { key: 'html', label: 'HTML', icon: faFile },
            { key: 'xml', label: 'XML', icon: faCode },
            { key: 'text', label: 'Text', icon: faFile },
            { key: 'image', label: 'Image', icon: faFile },
            { key: 'other', label: 'Other', icon: faFile }
          ].map(({ key, label, icon }) => {
            const count = counts.contentTypes?.[key] || 0;
            if (count === 0) return null;

            return (
              <button
                key={key}
                onClick={() => toggleContentType(key)}
                className={`${badgeClass(contentTypes.includes(key))} ${
                  isDarkMode ? 'bg-purple-700 text-white hover:bg-purple-600' : 'bg-purple-600 text-white hover:bg-purple-700'
                }`}
              >
                <FontAwesomeIcon icon={icon} className="mr-1.5" />
                {label}
                <span className="ml-1.5 opacity-75">({count})</span>
              </button>
            );
          })}

          {/* Separator */}
          <div className={`h-6 w-px ${isDarkMode ? 'bg-gray-600' : 'bg-gray-300'}`}></div>

          {/* Reset Button */}
          <button
            onClick={reset}
            className={`px-1.5 py-0.5 rounded text-[10px] ${
              isDarkMode
                ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
            }`}
          >
            <FontAwesomeIcon icon={faUndo} className="mr-1" />
            Reset
          </button>

          {/* Separator */}
          <div className={`h-6 w-px ${isDarkMode ? 'bg-gray-600' : 'bg-gray-300'}`}></div>

          {/* Search field */}
          <input
            type="text"
            placeholder="Search URL, request, response..."
            value={searchText || ''}
            onChange={(e) => onSearchChange(e.target.value)}
            className={`px-2 py-0.5 rounded text-[10px] w-48 ${
              isDarkMode
                ? 'bg-gray-700 text-gray-300 placeholder-gray-500 border border-gray-600 focus:border-blue-500'
                : 'bg-white text-gray-700 placeholder-gray-400 border border-gray-300 focus:border-blue-500'
            } focus:outline-none focus:ring-1 focus:ring-blue-500`}
          />

          {/* Separator */}
          <div className={`h-6 w-px ${isDarkMode ? 'bg-gray-600' : 'bg-gray-300'}`}></div>

          {/* Exclude static files checkbox */}
          <label className="text-[10px] flex items-center gap-1 cursor-pointer">
            <input
              type="checkbox"
              checked={excludeStatic}
              onChange={e => onExcludeStaticChange(e.target.checked)}
              className="rounded"
            />
            <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Exclude static</span>
          </label>

          {/* Result count - pushed to right */}
          <span className={`text-[10px] ml-auto ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            {filteredCount} of {totalEndpoints}
          </span>
      </div>
    </div>
  );
};

export default AuthSmashMatrixFilters;
